import { useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, Flag, Tag, UserRound } from 'lucide-react';
import { aggregate, formatAggregate, laneChange, laneKeyOf, buildLanes, type BoardSnapshot, type CardSummary, type Column, type Lane, type LaneMode, type Member } from '@kaba/shared';
import { useLaneChange } from '../../lib/board';
import { Avatar, columnLook, cx, LABEL_BG } from '../ui/ui';
import type { Layout, LaneCellProps, LaneSetup } from './BoardDnd';
import { AddCard, CountBadge, RulesHint } from './Columns';

const PRIORITY_DOT = ['', 'bg-slate-400', 'bg-amber-500', 'bg-orange-600', 'bg-rose-600'];

function LaneIcon({ lane, snapshot }: { lane: Lane; snapshot: BoardSnapshot }) {
  if (lane.kind === 'member') {
    const m = snapshot.members.find((x) => x.id === lane.refId);
    return m ? <Avatar user={m as Member} size={24} /> : <UserRound size={18} className="text-[var(--ink-soft)]" />;
  }
  if (lane.kind === 'priority') return <Flag size={16} className={cx('rounded-full', PRIORITY_DOT[Number(lane.key)]?.replace('bg-', 'text-'))} />;
  if (lane.kind === 'label') {
    const l = snapshot.labels.find((x) => x.id === lane.refId);
    return <span className={cx('size-3.5 rounded-full', l ? LABEL_BG[l.color] : 'bg-slate-400')} aria-hidden />;
  }
  return <span className="size-3.5 rounded-full border-2 border-dashed border-[var(--ink-soft)]" aria-hidden />;
}

/** Baut die Zeilen-Einstellung für BoardDnd – null, wenn keine Zeilen gewählt sind */
export function useLaneSetup(opts: {
  snapshot: BoardSnapshot | undefined;
  mode: LaneMode | undefined;
  emptyLanes: boolean;
  layout: Layout;
  visibleCards: CardSummary[];
  canEdit: boolean;
}): LaneSetup | null {
  const change = useLaneChange(opts.snapshot?.board.id ?? '');
  const changeRef = useRef(change);
  changeRef.current = change;
  const { snapshot, mode, layout, canEdit, emptyLanes, visibleCards } = opts;
  // Stabil halten: BoardDnd rechnet bei jeder neuen Einstellung die Ablageziele neu
  return useMemo(() => {
  if (!mode || !snapshot) return null;
  const labelOrder = snapshot.labels.map((l) => l.id);
  const aggFields = snapshot.fields.filter((f) => !!f.config?.aggregate);
  const list = buildLanes(mode, { cards: visibleCards, members: snapshot.members, labels: snapshot.labels, emptyLanes });
  const apply = (card: CardSummary, from: string, to: string) => {
    const c = laneChange(card, mode, from, to);
    if (c) changeRef.current(card, c);
  };
  const setup: LaneSetup = {
    list,
    keyOf: (c) => laneKeyOf(c, mode, labelOrder),
    onChange: apply,
    className:
      layout === 'board'
        ? 'kaba-board-scroll h-full overflow-auto px-4 pt-1 pb-24 sm:pb-6'
        : 'kaba-board-scroll mx-auto h-full w-full max-w-3xl overflow-y-auto px-3 py-3 pb-28',
    renderHeader: ({ lane, count, collapsed, toggle }) => (
      <button
        onClick={toggle}
        aria-expanded={!collapsed}
        className={cx(
          // Deckende Fläche: auch auf einem Boardhintergrund lesbar
          'flex min-h-11 items-center gap-2 rounded-lg bg-[var(--surface)] pr-3 pl-1 text-left text-sm font-semibold hover:bg-[var(--column)]',
          layout === 'list' && 'w-full',
        )}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        <LaneIcon lane={lane} snapshot={snapshot} />
        <span className={cx('truncate', lane.kind === 'none' && 'font-medium text-[var(--ink-soft)]')}>{lane.title}</span>
        <span className="rounded-full bg-black/5 px-2 text-xs font-medium text-[var(--ink-soft)] dark:bg-white/10">{count}</span>
        {/* 0.82: Zwischensummen der ausgewerteten Felder je Zeile */}
        {aggFields.map((f) => {
          const text = formatAggregate(f, aggregate(f, visibleCards.filter((c) => laneKeyOf(c, mode, labelOrder) === lane.key).map((c) => c.custom?.[f.id])));
          return text ? (
            <span key={f.id} className="hidden truncate rounded-md bg-black/5 px-1.5 text-[11px] font-normal tabular-nums text-[var(--ink-soft)] sm:inline dark:bg-white/10">
              {f.name}: {text}
            </span>
          ) : null;
        })}
      </button>
    ),
    renderColumnHeader: ({ column, count, totalCount }) => (
      <div className={cx('flex min-h-9 items-center gap-1.5 rounded-xl px-3', columnLook(column.color).className)} style={columnLook(column.color).style}>
        <h2 className="flex-1 truncate text-sm font-semibold">{column.title}</h2>
        <RulesHint column={column} />
        <CountBadge column={column} total={totalCount} shown={count} />
      </div>
    ),
    renderCell: (p) => <LaneCell p={p} layout={layout} boardId={snapshot.board.id} canEdit={canEdit} onCreated={(card) => apply(card, laneKeyOf(card, mode, labelOrder), p.lane.key)} />,
  };
  return setup;
  }, [snapshot, mode, emptyLanes, layout, visibleCards, canEdit]);
}

function LaneCell({ p, layout, boardId, canEdit, onCreated }: { p: LaneCellProps; layout: Layout; boardId: string; canEdit: boolean; onCreated: (c: CardSummary) => void }) {
  const { column, cards, children, isOver } = p;
  if (layout === 'list')
    return (
      <div className={cx('overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--panel)]', isOver && 'ring-2 ring-accent/40')}>
        <div className={cx('flex min-h-9 items-center gap-1.5 px-3 text-xs font-semibold', columnLook(column.color, { bar: false }).className)} style={columnLook(column.color, { bar: false }).style}>
          <span className="flex-1 truncate">{column.title}</span>
          <span className="text-[var(--ink-soft)]">{cards.length}</span>
        </div>
        <div className="flex flex-col divide-y divide-[var(--line)]">{children}</div>
        {canEdit && (
          <div className="border-t border-[var(--line)] px-1.5 py-1">
            <AddCard boardId={boardId} columnId={column.id} compact onCreated={onCreated} />
          </div>
        )}
      </div>
    );
  return (
    <div className={cx('flex h-full min-h-20 flex-col gap-2 rounded-2xl bg-[var(--column)] p-2', isOver && 'ring-2 ring-accent/40')}>
      {children}
      {cards.length === 0 && (
        <div className="flex min-h-12 items-center justify-center rounded-xl border-2 border-dashed border-[var(--line)] text-xs text-[var(--ink-soft)]">Hierher ziehen</div>
      )}
      {canEdit && <AddCard boardId={boardId} columnId={column.id} compact onCreated={onCreated} />}
    </div>
  );
}

